import { gql } from '@apollo/client';
import { client } from './client';
import { TASK_FIELDS, COMMENT_FIELDS, ASSIGNMENTS_FIELDS, REMINDER_FIELDS } from './task.fragment';

export const fetchCheckIn = async () => {
  const result = await client.query({
    query: gql`
      query notificationInbox {
        notificationInbox {
          checkin {
            unreadCount
            focusTaskCount
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  return result;
};

export const fetchNotifications = async (variables) => {
  const result = await client.query({
    query: gql`
      query notificationInbox($page: Int, $pageSize: Int, $unreadOnly: Boolean) {
        notificationInbox {
          checkin {
            unreadCount
            focusTaskCount
          }
          notifications(page: $page, pageSize: $pageSize, unreadOnly: $unreadOnly) {
            start
            limit
            total
            pages
            currentPage
            items {
              id
              sender {
                username
                fullname
                firstName
                lastName
                initials
                profileImage {
                  resolutions
                }
              }
              image {
                resolutions
              }
              title
              body
              bodyTruncator
              externalSenderName
              externalSenderEmail
              viewName
              viewParams
              otherData
              isRead
              needsSend
              kind
              createdAt
              updatedAt
              wedding {
                id
                name
                weddingPartyName
              }
              url
            }
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
  });

  return result;
};

export const fetchTask = async (variables) => {
  const result = await client.query({
    query: gql`
      ${COMMENT_FIELDS}
      ${REMINDER_FIELDS}
      query task(
        $id: Int!
      ) {
        task(
          id: $id
        ) {
          comments {
            ...commentInfo
          }
          reminders {
            ...reminderInfo
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return result.data;
};

export const fetchTasks = async () => {
  const result = await client.query({
    query: gql`
      ${TASK_FIELDS}
      query notificationInbox {
        notificationInbox {
          tasksInfo {
            loginTasks {
              assignmentOptions {
                label
                id
              }
              tasks {
                ...taskInfo
              }
            }
            weddings {
              id
              name
              organizationId
              assignmentOptions {
                label
                id
                organizationId
              }
              tasks {
                ...taskInfo
              }
            }
            leads {
              id
              name
              assignmentOptions {
                label
                id
              }
              tasks {
                ...taskInfo
              }
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  return result;
};

export const fetchGeneralTasks = async () => {
  const result = await client.query({
    query: gql`
      ${TASK_FIELDS}
      query notificationInbox {
        notificationInbox {
          tasksInfo {
            loginTasks {
              assignmentOptions {
                label
                id
              }
              tasks {
                ...taskInfo
              }
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  return result;
};

export const fetchProjectTasks = async () => {
  const result = await client.query({
    query: gql`
      ${TASK_FIELDS}
      query notificationInbox {
        notificationInbox {
          tasksInfo {
            weddings {
              id
              name
              organizationId
              assignmentOptions {
                label
                id
                organizationId
              }
              tasks {
                ...taskInfo
              }
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  return result;
};

export const fetchLeadTasks = async () => {
  const result = await client.query({
    query: gql`
      ${TASK_FIELDS}
      query notificationInbox {
        notificationInbox {
          tasksInfo {
            leads {
              id
              name
              assignmentOptions {
                label
                id
              }
              tasks {
                ...taskInfo
              }
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  return result;
};

export const fetchFocusTasks = async () => {
  const result = await client.query({
    query: gql`
      ${ASSIGNMENTS_FIELDS}
      query notificationInbox {
        notificationInbox {
          tasksInfo {
            assignedTasks {
              id
              name
              details
              completed
              dueDate
              timeframe
              assignmentOptions {
                label
                id
              }
              hasWedding
              weddingItem {
                clientVisible
                wedding {
                  id
                  organizationId
                  firstnames
                  shortname
                  projectType
                }
              }
              hasLead
              lead {
                id
                contactFullName
              }
              hasComments
              assignments {
                ...assignmentInfo
              }
              currentLoginAssignment {
                focusLoginOrder
              }
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  return result;
};

export const readAllNotifications = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation markAllNotificationsAsRead {
        markAllNotificationsAsRead {
          success
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const readNotification = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation markNotificationAsRead(
        $id: Int
      ) {
        markNotificationAsRead(
          id: $id
        ) {
          notification {
            id
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

// Task Login
export const createLoginTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      ${TASK_FIELDS}
      mutation createLoginTask(
        $focusLevel: Int
        $input: TaskInput
        $referenceId: Int
      ) {
        createLoginTask(
          focusLevel: $focusLevel
          input: $input
          referenceId: $referenceId
        ) {
          ...taskInfo
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const updateLoginTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      ${TASK_FIELDS}
      mutation updateLoginTask(
        $id: Int!
        $input: UpdateTaskInput
      ) {
        updateLoginTask(
          id: $id
          input: $input
        ) {
          ...taskInfo
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const deleteLoginTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation deleteLoginTask(
        $id: Int!
      ) {
        deleteLoginTask(
          id: $id
        ) {
          deleted
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

// Task Lead
export const createLeadTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      ${TASK_FIELDS}
      mutation createLeadTask(
        $input: LeadTaskInput
        $leadId: Int!
        $referenceId: Int
      ) {
        createLeadTask(
          input: $input
          leadId: $leadId
          referenceId: $referenceId
        ) {
          ...taskInfo
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const updateLeadTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      ${TASK_FIELDS}
      mutation updateLeadTask(
        $leadId: Int!
        $taskId: Int!
        $input: LeadTaskUpdateInput
      ) {
        updateLeadTask(
          leadId: $leadId
          taskId: $taskId
          input: $input
        ) {
          ...taskInfo
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const deleteLeadTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation deleteLeadTask(
        $leadId: Int!
        $taskId: Int!
      ) {
        deleteLeadTask(
          leadId: $leadId
          taskId: $taskId
        ) {
          deleted
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

// Task Wedding
export const createWeddingTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      ${TASK_FIELDS}
      mutation createWeddingTask(
        $input: WeddingTaskInput
        $weddingId: Int!
        $referenceId: Int
      ) {
        createWeddingTask(
          input: $input
          weddingId: $weddingId
          referenceId: $referenceId
        ) {
          ...taskInfo
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const updateWeddingTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      ${TASK_FIELDS}
      mutation updateWeddingTask(
        $weddingId: Int!
        $taskId: Int!
        $input: WeddingTaskUpdateInput
      ) {
        updateWeddingTask(
          weddingId: $weddingId
          taskId: $taskId
          input: $input
        ) {
          ...taskInfo
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const deleteWeddingTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation deleteWeddingTask(
        $weddingId: Int!
        $taskId: Int!
      ) {
        deleteWeddingTask(
          weddingId: $weddingId
          taskId: $taskId
        ) {
          deleted
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

// Task Assignment
export const addTaskAssignment = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation addTaskAssignment(
        $input: AssignmentInput!
        $taskId: Int!
      ) {
        addTaskAssignment(
          input: $input
          taskId: $taskId
        ) {
          taskId
          loginId
          focusLevel
          loginOrder
          login {
            id,
            fullname
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const updateTaskAssignment = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation updateTaskAssignment(
        $input: AssignmentInput!
        $taskId: Int!
      ) {
        updateTaskAssignment(
          input: $input
          taskId: $taskId
        ) {
          taskId
          loginId
          focusLevel
          loginOrder
          login {
            id,
            fullname
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const dropTaskAssignment = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation dropTaskAssignment(
        $loginId: Int!
        $taskId: Int!
      ) {
        dropTaskAssignment(
          loginId: $loginId
          taskId: $taskId
        ) {
          deleted
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

// Task Comment
export const createTaskComment = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation createTaskComment(
        $input: CommentInput!
        $taskId: Int!
      ) {
        createTaskComment(
          input: $input
          taskId: $taskId
        ) {
          id
          createdAt
          updatedAt
          kind
          body
          html
          login {
            id
            firstname
            lastname
            profileImage {
              resolutions
            }
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const updateTaskComment = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation updateTaskComment(
        $commentId: Int!
        $input: CommentInput
        $taskId: Int!
      ) {
        updateTaskComment(
          commentId: $commentId
          input: $input
          taskId: $taskId
        ) {
          id
          createdAt
          updatedAt
          kind
          body
          html
          login {
            id
            firstname
            lastname
            profileImage {
              resolutions
            }
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const deleteTaskComment = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation deleteTaskComment(
        $commentId: Int!
        $taskId: Int!
      ) {
        deleteTaskComment(
          commentId: $commentId
          taskId: $taskId
        ) {
          deleted
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const orderBeforeAssignedTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation orderBeforeAssignedTask(
        $referenceId: Int
        $taskId: Int!
      ) {
        orderBeforeAssignedTask(
          referenceId: $referenceId
          taskId: $taskId
        ) {
          taskId
          loginOrder
          focusLoginOrder
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};

export const orderBeforeFocusedTask = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation orderBeforeFocusedTask(
        $referenceId: Int
        $taskId: Int!
      ) {
        orderBeforeFocusedTask(
          referenceId: $referenceId
          taskId: $taskId
        ) {
          taskId
          loginOrder
          focusLoginOrder
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return results;
};
